import React, { useEffect } from "react";
import {
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { FlowStep } from "../../../types";

const schema = z.object({
  name: z.string().min(1, "Nome obbligatorio"),
  description: z.string().optional(),
  channel: z.enum(["whatsapp", "*"]),
  webhook_url: z.string().url("URL non valido").optional().or(z.literal("")),
  first_step_id: z.string().min(1, "Primo step obbligatorio"),
});

type FormData = z.infer<typeof schema>;

export interface FlowMetaState {
  name: string;
  description: string;
  channel: "whatsapp" | "*";
  webhook_url: string;
  first_step_id: string;
}

interface Props {
  meta: FlowMetaState;
  steps: FlowStep[];
  onChange: (meta: FlowMetaState) => void;
}

export const FlowMetaForm = ({ meta, steps, onChange }: Props) => {
  const {
    control,
    watch,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: meta,
    mode: "onChange",
  });

  // Sync external meta → form (e.g. when flow loaded from BE)
  useEffect(() => {
    reset(meta);
  }, [meta.name, meta.channel]); // eslint-disable-line react-hooks/exhaustive-deps

  // Propagate form changes upward in real-time
  useEffect(() => {
    const sub = watch((values) => {
      onChange({
        name: values.name ?? "",
        description: values.description ?? "",
        channel: values.channel ?? "whatsapp",
        webhook_url: values.webhook_url ?? "",
        first_step_id: values.first_step_id ?? "",
      });
    });
    return () => sub.unsubscribe();
  }, [watch, onChange]);

  return (
    <div className="flex-col gap-2">
      <Controller
        name="name"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            fullWidth
            size="small"
            label="Nome flow"
            error={!!errors.name}
            helperText={errors.name?.message}
          />
        )}
      />
      <Controller
        name="description"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            fullWidth
            size="small"
            label="Descrizione (opzionale)"
          />
        )}
      />
      <div className="flex-row gap-2">
        <Controller
          name="channel"
          control={control}
          render={({ field }) => (
            <FormControl fullWidth size="small">
              <InputLabel>Canale</InputLabel>
              <Select {...field} label="Canale" value={field.value ?? "whatsapp"}>
                <MenuItem value="whatsapp">whatsapp</MenuItem>
                <MenuItem value="*">* (tutti)</MenuItem>
              </Select>
            </FormControl>
          )}
        />
        <Controller
          name="first_step_id"
          control={control}
          render={({ field }) => (
            <FormControl fullWidth size="small" error={!!errors.first_step_id}>
              <InputLabel>Primo step</InputLabel>
              <Select
                {...field}
                label="Primo step"
                value={field.value ?? ""}
              >
                <MenuItem value="">— seleziona —</MenuItem>
                {steps.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    [{s.type}] {s.id.slice(0, 8)}… (ord. {s.order})
                  </MenuItem>
                ))}
              </Select>
              {errors.first_step_id && (
                <FormHelperText>{errors.first_step_id.message}</FormHelperText>
              )}
            </FormControl>
          )}
        />
      </div>
      <Controller
        name="webhook_url"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            fullWidth
            size="small"
            label="Webhook URL (opzionale)"
            placeholder="https://..."
            error={!!errors.webhook_url}
            helperText={errors.webhook_url?.message}
          />
        )}
      />
    </div>
  );
};
